// renameCte — VQE-Edit edit-action: coordinate-rename a CTE definition + every
// reference in the statement via SR-Edit's `rename-cte` op. SR-Edit walks the
// statement body (and sibling CTE bodies) to find references whose case-folded
// identifier matches the CTE's name; emits one PlannedByteEdit per match plus
// the definition itself.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.

import { commitEdit, type CommitEditCtx } from '../commitEdit';

export interface RenameCteOptions {
  /** Total number of CTEs in the WITH clause — used for index-bounds validation. */
  readonly cteCount?: number;
  /** Pre-validate the new name doesn't shadow an existing identifier in scope. */
  readonly conflictsWithExisting?: boolean;
  /** Confirm-on-conflict callback invoked when `conflictsWithExisting` is true. */
  readonly confirmIfConflict?: () => boolean | Promise<boolean>;
}

const IDENT_PATTERN = /^[A-Za-z_][A-Za-z0-9_$]*$|^"[^"]*"$|^`[^`]*`$|^\[[^\]]*\]$/;

export async function renameCte(
  index: number,
  newName: string,
  ctx: CommitEditCtx,
  options: RenameCteOptions = {},
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`renameCte: index must be a non-negative integer (got ${index})`);
  }
  if (options.cteCount !== undefined) {
    if (options.cteCount === 0) throw new Error('renameCte: WITH clause has no CTEs');
    if (index >= options.cteCount) {
      throw new Error(
        `renameCte: index ${index} out of range (WITH clause has ${options.cteCount} CTE(s))`,
      );
    }
  }
  const trimmed = newName.trim();
  if (trimmed.length === 0) {
    throw new Error('renameCte: newName must not be empty');
  }
  if (!IDENT_PATTERN.test(trimmed)) {
    throw new Error(
      `renameCte: newName "${trimmed}" is not a valid identifier (use bare letters/digits/underscores or a quoted form)`,
    );
  }
  if (options.conflictsWithExisting && options.confirmIfConflict) {
    const proceed = await options.confirmIfConflict();
    if (!proceed) return;
  }
  await commitEdit({ op: 'rename-cte', index, newName: trimmed }, ctx);
}
